import React from "react";
import Model from "@tripian/model";
import { SvgIcons } from "@tripian/react";

interface ITourListCard {
  product: Model.TourSearchProduct;
  providerName: string;
  onSelect: (product: Model.TourSearchProduct) => void;
  t: (value: Model.TranslationKey) => string;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  TRY: "₺",
};

const price = (amount: number, currency?: string): string => {
  const symbol =
    (currency && CURRENCY_SYMBOLS[currency.toUpperCase()]) || currency || "";
  return `${symbol}${amount}`;
};

/** Dakikayı "90 dk" / "2 sa 30 dk" biçimine çevirir. */
const duration = (minutes?: number): string | undefined => {
  if (!minutes || minutes <= 0) return undefined;
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  if (hours === 0) return `${rest}′`;
  return rest === 0 ? `${hours}s` : `${hours}s ${rest}′`;
};

const TourListCard: React.FC<ITourListCard> = ({
  product,
  providerName,
  onSelect,
  t,
}) => {
  const image =
    product.images?.find(img => img.is_cover)?.url || product.images?.[0]?.url;
  const durationLabel = duration(product.duration);
  const tags = (product.tags || []).slice(0, 3);

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyPress={() => {}}
      onClick={() => onSelect(product)}
      className="group flex flex-col sm:flex-row gap-4 bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
    >
      <div className="sm:w-56 sm:flex-shrink-0 h-44 sm:h-auto bg-gray-100">
        {image ? (
          <img
            src={image}
            alt={product.title}
            referrerPolicy="no-referrer"
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : null}
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-2 p-4 sm:pl-0">
        <h3 className="text-base font-semibold text-gray-900 line-clamp-2">
          {product.title}
        </h3>

        <div className="flex items-center gap-3 text-sm text-gray-600">
          {product.rating > 0 && (
            <span className="flex items-center gap-1">
              <SvgIcons.Star2 />
              <span className="font-medium text-gray-800">
                {product.rating}
              </span>
              {product.ratingCount > 0 && (
                <span className="text-gray-500">({product.ratingCount})</span>
              )}
            </span>
          )}
          {durationLabel && <span>{durationLabel}</span>}
          {providerName && (
            <span className="text-gray-500 truncate">{providerName}</span>
          )}
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tags.map(tag => (
              <span
                key={tag}
                className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="mt-auto flex items-end justify-between gap-4 pt-2">
          <div>
            {product.price > 0 && (
              <>
                <div className="text-lg font-semibold text-gray-900">
                  {price(product.price, product.currency)}
                </div>
                <div className="text-xs text-gray-500">
                  {t(
                    "trips.myTrips.localExperiences.tourDetails.experience.perPerson"
                  )}
                </div>
              </>
            )}
          </div>
          <span className="px-5 py-2 rounded-full bg-primary-color text-white text-sm font-medium whitespace-nowrap">
            {t("trips.myTrips.localExperiences.tourDetails.bookNow")}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TourListCard;
